/**
 * Room typing — assign a RoomType to each BSP cell so props + population can be
 * placed logically. Phase 1c: a single generic office floor (multi-floor purpose
 * — grind vs. important vs. basement — is Phase 1d). Types are chosen by interior
 * area + a seeded roll, with a few forced so a floor always has the essentials
 * (a lobby at spawn, a utility room for the fusebox, a restroom, a security office).
 *
 * Pure + deterministic.
 */

import { floorH, floorW, type Rect } from './bsp';

export type RoomType =
  | 'stairwell'
  | 'corridor'
  | 'lobby'
  | 'open-office'
  | 'private-office'
  | 'meeting'
  | 'break'
  | 'restroom'
  | 'storage'
  | 'utility'
  | 'security'
  | 'records'
  | 'server'
  // --- service / plant rooms (Phase 2c; basement-biased, forced into
  //     existence by the building's required-service list) ---
  | 'boiler'
  | 'hvac'
  | 'pump'
  | 'fire'
  | 'elevator';

const LABEL: Record<RoomType, string> = {
  stairwell: 'Stairwell',
  corridor: 'Corridor',
  lobby: 'Lobby',
  'open-office': 'Open Office',
  'private-office': 'Office',
  meeting: 'Meeting Room',
  break: 'Break Room',
  restroom: 'Restroom',
  storage: 'Storage',
  utility: 'Utility',
  security: 'Security',
  records: 'Records',
  server: 'Server Room',
  boiler: 'Boiler Room',
  hvac: 'Mechanical',
  pump: 'Pump Room',
  fire: 'Fire Control',
  elevator: 'Elevator Machine',
};

/** Room types that exist to HOUSE a building service's plant. They are never
 *  rolled at random — the required-service list forces them onto a floor (see
 *  `services.ts` / `building.ts`), which is what guarantees a sabotage target. */
export const PLANT_ROOMS: readonly RoomType[] = ['boiler', 'hvac', 'pump', 'fire', 'elevator'];

export function roomLabel(t: RoomType): string {
  return LABEL[t];
}

// Type pools by interior area bucket (props/population read the assigned type).
const LARGE: RoomType[] = ['open-office', 'open-office', 'records', 'meeting'];
const MEDIUM: RoomType[] = ['private-office', 'meeting', 'break', 'server', 'open-office'];
const SMALL: RoomType[] = ['restroom', 'storage', 'utility', 'security', 'private-office'];

function pick(pool: RoomType[], rng: () => number): RoomType {
  return pool[Math.floor(rng() * pool.length)]!;
}

/**
 * Classify each cell (index-aligned with the array) into a RoomType. `forced`
 * pins specific cells (the stairwell core, the entrance lobby); the rest are
 * chosen by interior area. Guarantees a utility, restroom, and security among the
 * unforced cells, and seats any requested PLANT rooms (a service's machine room)
 * on the smallest cells — plant rooms are cramped, and it keeps the big cells
 * free for the rooms the floor is actually for.
 */
export function classifyRooms(
  cells: Rect[],
  rng: () => number,
  forced: (i: number) => RoomType | null,
  plants: readonly RoomType[] = [],
): RoomType[] {
  const area = cells.map((c) => Math.max(1, floorW(c)) * Math.max(1, floorH(c)));
  const types: RoomType[] = cells.map((_c, i) => {
    const f = forced(i);
    if (f) return f;
    const a = area[i]!;
    if (a >= 90) return pick(LARGE, rng);
    if (a >= 40) return pick(MEDIUM, rng);
    return pick(SMALL, rng);
  });

  const order = cells
    .map((_c, i) => i)
    .filter((i) => !forced(i))
    .sort((p, q) => area[p]! - area[q]!);
  // Plant rooms first — a service with no machine room has nothing to sabotage,
  // so they outrank the essentials for the small cells. Claimed smallest-first.
  const claimed = new Set<number>();
  for (const plant of plants) {
    const already = types.findIndex((t) => t === plant);
    if (already >= 0) {
      // Already rolled one at random — CLAIM it, or the essentials pass below
      // is free to overwrite it and the floor ends up with neither the plant
      // room nor the essential's original slot.
      claimed.add(already);
      continue;
    }
    const slot = order.find((i) => !claimed.has(i));
    if (slot === undefined) break; // floor too small for the rest — caller re-homes them
    claimed.add(slot);
    types[slot] = plant;
  }
  // Then force the essentials onto the smallest remaining unforced cells.
  const essentials: RoomType[] = ['utility', 'restroom', 'security'];
  for (const need of essentials) {
    if (types.includes(need)) continue;
    const slot = order.find((i) => !claimed.has(i) && !essentials.includes(types[i]!));
    if (slot === undefined) continue;
    claimed.add(slot);
    types[slot] = need;
  }
  return types;
}
